import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, CheckCircle, Calendar } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { courseCategories } from "@/data/courses";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConsultancyPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", course_interest: "", message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please fill in your name and phone number");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings/consultancy`, form);
      setSubmitted(true);
      toast.success("Consultancy booked successfully! We'll contact you soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="consultancy-success">
        <section className="bg-vedanti-navy py-12 md:py-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.15)_0%,_transparent_60%)]"></div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
          <div className="container-main relative z-10">
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white">Book <span className="text-vedanti-orange">Free Consultancy</span></h1>
          </div>
        </section>
        <section className="section-padding bg-white">
          <div className="container-main max-w-lg text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-vedanti-navy mb-3">Booking Confirmed!</h2>
            <p className="text-slate-600 mb-6">Thank you for booking a consultancy with Vedanti Academy. Our team will contact you shortly to confirm the details.</p>
            <a href="tel:+918308776505" className="inline-flex items-center gap-2 text-vedanti-orange font-semibold text-lg hover:text-vedanti-navy transition-colors">
              <Phone size={20} /> +91 8308776505
            </a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="consultancy-page">
      <Helmet>
        <title>Free Career Consultancy for Exams | Vedanti Academy</title>
        <meta name="description" content="Get free personalized career guidance from Vedanti Academy experts. We help you choose the right competitive exam path - Banking, SSC, MBA, Railway or Insurance. Book your free session today!" />
        <meta name="keywords" content="free career consultancy, exam guidance, competitive exam counselling, banking career guidance, Vedanti Academy consultancy" />
        <meta property="og:title" content="Book Free Consultancy | Vedanti Academy" />
        <meta property="og:description" content="Get personalized guidance for your competitive exam journey. Our experts help you choose the right path." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vedantiacademy.com/consultancy" />
        <link rel="canonical" href="https://vedantiacademy.com/consultancy" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Free Career Consultancy - Vedanti Academy",
          "description": "Get free personalized career guidance for competitive exam preparation from Vedanti Academy experts.",
          "url": "https://vedantiacademy.com/consultancy",
          "provider": {"@type": "EducationalOrganization", "name": "Vedanti Academy", "url": "https://vedantiacademy.com"},
          "serviceType": "Career Consultancy",
          "offers": {"@type": "Offer", "price": "0", "priceCurrency": "INR", "description": "Free consultancy session"}
        })}</script>
      </Helmet>
      <section className="bg-vedanti-navy py-12 md:py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.15)_0%,_transparent_60%)]"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
        <div className="container-main relative z-10">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white">Book <span className="text-vedanti-orange">Free Consultancy</span></h1>
          <p className="text-slate-300 mt-3 text-lg">Get personalized guidance for your exam preparation journey</p>
        </div>
      </section>

      <section className="section-padding bg-white" data-testid="consultancy-info-section">
        <div className="container-main max-w-3xl prose prose-slate">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-vedanti-navy">Free Career Consultancy for Competitive Exam Aspirants</h2>
          <p>Choosing the right competitive exam is one of the most important decisions in a student&rsquo;s career, and the wrong choice can cost years of preparation. Vedanti Academy&rsquo;s free consultancy service is designed to remove that confusion. Our experienced mentors sit with you one-on-one to understand your academic background, strengths, interests and long-term goals before recommending the exam path that suits you best — whether that is Banking, SSC, Railway, MBA CET or Insurance.</p>
          <h3 className="font-heading text-xl font-bold text-vedanti-navy">What Our Consultancy Covers</h3>
          <p>In a single session we help you compare exams like IBPS PO/Clerk, SBI PO/Clerk, RBI, SSC CGL/CHSL, Railway NTPC and MAH MBA CET on the basis of eligibility, difficulty, number of vacancies, salary and growth prospects. We then map out a realistic timeline, recommend the right batch and study material, and explain how our Vedic Math shortcuts and 360-degree hybrid learning model can accelerate your preparation. If you are a working professional or a college student, we will show you how weekend and evening batches fit around your commitments.</p>
          <h3 className="font-heading text-xl font-bold text-vedanti-navy">Guidance You Can Trust</h3>
          <p>Our advice is honest and student-first — we would rather point you to the exam where you have the best chance of selection than sell you a course. This is the same mentorship that has helped 31+ Vedanti achievers secure jobs in leading banks, Indian Railways and LIC, and that has earned us 500+ 5-star Google reviews from families across Badlapur, Ambernath, Ulhasnagar, Karjat, Neral, Vangni and Khopoli.</p>
          <p>To book your free consultancy, simply complete the form above and our team will call you back at a time that works for you. Prefer to talk right away? Call or WhatsApp us on +91&nbsp;8308776505. There is no cost and no pressure — just clear, expert guidance to help you start your journey with confidence.</p>
        </div>
      </section>

      <section className="section-padding bg-slate-50">
        <div className="container-main max-w-2xl">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-vedanti-orange/10 rounded-xl flex items-center justify-center">
                <Calendar size={24} className="text-vedanti-orange" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl text-vedanti-navy">Consultancy Booking Form</h2>
                <p className="text-sm text-slate-500">Fill out the form and we&apos;ll get back to you</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="consultancy-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-slate-700 mb-1.5 block">Full Name *</Label>
                  <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Enter your full name" required data-testid="consultancy-name-input" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-slate-700 mb-1.5 block">Phone Number *</Label>
                  <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 XXXXXXXXXX" required data-testid="consultancy-phone-input" />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Course Interest</Label>
                <Select onValueChange={(v) => setForm({ ...form, course_interest: v })}>
                  <SelectTrigger data-testid="consultancy-course-select">
                    <SelectValue placeholder="Select course" />
                  </SelectTrigger>
                  <SelectContent>
                    {courseCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="text-sm font-medium text-slate-700 mb-1.5 block">Message (Optional)</Label>
                <Textarea id="message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your preparation goals..." rows={3} data-testid="consultancy-message-input" />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-vedanti-orange text-white hover:bg-vedanti-orange/90 rounded-lg px-6 py-3.5 font-bold text-lg transition-all shadow-lg shadow-orange-500/20 disabled:opacity-60"
                data-testid="consultancy-submit-btn"
              >
                {loading ? "Submitting..." : "Book Free Consultancy"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
